/**
 * Centralized Environment Configuration
 * Single source of truth for all environment variables
 * Provides type safety and validation for environment variables
 */

/// <reference types="vite/client" />

// Environment variable validation helpers
const getEnvVar = (key: string, defaultValue?: string): string => {
  const value = import.meta.env[key as keyof ImportMetaEnv];
  
  // Debug logging removed - use logger if needed
  
  if (value === undefined || value === '') {
    if (defaultValue !== undefined) {
      return defaultValue;
    }
    throw new Error(`Environment variable ${key} is required but not defined`);
  }
  return value;
};

const getEnvBoolean = (key: string, defaultValue = false): boolean => {
  const value = import.meta.env[key as keyof ImportMetaEnv];
  if (value === undefined || value === '') {
    return defaultValue;
  }
  return value.toLowerCase() === 'true';
};

const getEnvNumber = (key: string, defaultValue?: number): number => {
  const value = import.meta.env[key as keyof ImportMetaEnv];
  if (value === undefined || value === '') {
    if (defaultValue !== undefined) {
      return defaultValue;
    }
    throw new Error(`Environment variable ${key} is required but not defined`);
  }
  const parsed = parseInt(value, 10);
  if (isNaN(parsed)) {
    throw new Error(`Environment variable ${key} must be a valid number`);
  }
  return parsed;
};

// Environment Types
export type Environment = 'development' | 'staging' | 'production' | 'test';

export interface AppEnvironment {
  // Application
  app: {
    name: string;
    version: string;
    description: string;
    siteUrl: string;
    environment: Environment;
    isDevelopment: boolean;
    isProduction: boolean;
    isStaging: boolean;
    isTest: boolean;
    localDevMode: boolean;
  };

  // API Configuration
  api: {
    baseUrl: string;
    timeout: number;
    retries: number;
  };

  // Project Configuration
  project: {
    id: string;
    name: string;
  };

  // Feature Flags
  features: {
    analytics: boolean;
    errorReporting: boolean;
    performanceMonitoring: boolean;
    blog: boolean;
    projects: boolean;
  };

  // Security Configuration
  security: {
    enableCsrf: boolean;
    rateLimitEnabled: boolean;
  };

  // Logging Configuration
  logging: {
    level: string;
    enableConsole: boolean;
    enableRemote: boolean;
  };

  // Contact Information
  contact: {
    email: string;
    supportEmail: string;
    phone: string;
    address: string;
  };

  // Social Media URLs
  social: {
    twitter: string;
    linkedin: string;
    github: string;
    facebook: string;
    instagram: string;
  };
}

// Create environment configuration
const createEnvironment = (): AppEnvironment => {
  const environment = getEnvVar('VITE_ENVIRONMENT', 'development') as Environment;
  
  // Validate environment
  const validEnvironments: Environment[] = ['development', 'staging', 'production', 'test'];
  if (!validEnvironments.includes(environment)) {
    throw new Error(`Invalid environment: ${environment}. Must be one of: ${validEnvironments.join(', ')}`);
  }

  return {
    app: {
      name: getEnvVar('VITE_APP_NAME', 'ADITAS'),
      version: getEnvVar('VITE_APP_VERSION', '1.0.0'),
      description: getEnvVar('VITE_APP_DESCRIPTION', 'ADITAS: Created by MASTAQ Consulting'),
      siteUrl: getEnvVar('VITE_SITE_URL', 'http://localhost:3000').replace(/\/$/, ''),
      environment,
      isDevelopment: environment === 'development',
      isProduction: environment === 'production',
      isStaging: environment === 'staging',
      isTest: environment === 'test',
      localDevMode: import.meta.env.DEV
        && environment === 'development'
        && getEnvBoolean('VITE_ENABLE_LOCAL_DEV_MODE', false),
    },

    api: {
      baseUrl: getEnvVar('VITE_API_URL', 'http://localhost:5001/api/v1'),
      timeout: getEnvNumber('VITE_API_TIMEOUT', 10000),
      retries: getEnvNumber('VITE_API_RETRIES', 3),
    },

    project: {
      // Critical values - must be in .env, no defaults allowed
      id: getEnvVar('VITE_PROJECT_ID'), // Required - no default
      name: getEnvVar('VITE_PROJECT_NAME'), // Required - no default
    },

    features: {
      analytics: getEnvBoolean('VITE_ENABLE_ANALYTICS', false),
      errorReporting: getEnvBoolean('VITE_ENABLE_ERROR_REPORTING', false),
      performanceMonitoring: getEnvBoolean('VITE_ENABLE_PERFORMANCE_MONITORING', false),
      blog: getEnvBoolean('VITE_ENABLE_BLOG', true),
      projects: getEnvBoolean('VITE_ENABLE_PROJECTS', true),
    },

    security: {
      enableCsrf: getEnvBoolean('VITE_ENABLE_CSRF', true),
      rateLimitEnabled: getEnvBoolean('VITE_RATE_LIMIT_ENABLED', true),
    },

    logging: {
      level: getEnvVar('VITE_LOG_LEVEL', 'info'),
      enableConsole: getEnvBoolean('VITE_ENABLE_CONSOLE_LOGGING', true),
      enableRemote: getEnvBoolean('VITE_ENABLE_REMOTE_LOGGING', false),
    },

    contact: {
      email: getEnvVar('VITE_CONTACT_EMAIL', 'contact@aditas.com'),
      supportEmail: getEnvVar('VITE_SUPPORT_EMAIL', 'support@aditas.com'),
      phone: getEnvVar('VITE_CONTACT_PHONE', '+1 (555) 123-4567'),
      address: getEnvVar('VITE_CONTACT_ADDRESS', '123 Main St, City, State 12345'),
    },

    social: {
      twitter: getEnvVar('VITE_SOCIAL_TWITTER', 'https://twitter.com/aditas'),
      linkedin: getEnvVar('VITE_SOCIAL_LINKEDIN', 'https://linkedin.com/company/aditas'),
      github: getEnvVar('VITE_SOCIAL_GITHUB', 'https://github.com/aditas'),
      facebook: getEnvVar('VITE_SOCIAL_FACEBOOK', 'https://facebook.com/aditas'),
      instagram: getEnvVar('VITE_SOCIAL_INSTAGRAM', 'https://instagram.com/aditas'),
    },
  };
};

// Export singleton instance
export const ENV = createEnvironment();

// Helper functions for common environment checks
export const isDevelopment = (): boolean => ENV.app.isDevelopment;
export const isProduction = (): boolean => ENV.app.isProduction;
export const isStaging = (): boolean => ENV.app.isStaging;
export const isTest = (): boolean => ENV.app.isTest;

// Validation function to ensure all required environment variables are set
// Note: Using console directly to avoid circular dependency (utils -> constants -> environment)
export const validateEnvironment = (): void => {
  try {
    createEnvironment();
    // Use console directly to avoid circular dependency
    if (import.meta.env.DEV) {
      console.info('[Environment] Environment configuration validated successfully');
    }
  } catch (error) {
    // Use console directly to avoid circular dependency
    console.error('[Environment] Environment configuration validation failed', error);
    throw error;
  }
};

// Export default
export default ENV;
